import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import { FilterChangedEvent, GridApi } from 'ag-grid-community'
import 'ag-grid-enterprise'
import { useTheme } from '@mui/material/styles'
import { constants } from '../../common/constants'
import _ from 'lodash'
import { liquidityRiskByContractsColDef, liquidityRiskDashboardColDef } from '../../common/riskByCptyColDef'
import { getDarkRowStyle, getRowStyle } from '../../common/commonFunctions'
import { AgGridReact } from 'ag-grid-react'

interface AgGridProps {
  getGridRef?: any
  getGridApi?: any
  doesExternalFilterPass?: any
  isExternalFilterPresent?: any
  getContextMenuItems?: any
  globalSearch?: any
  rowData: any
  isByContractsGrid: boolean
}

const LiquidityRiskDashboardGrid = ({
  getGridApi,
  doesExternalFilterPass,
  isExternalFilterPresent,
  getContextMenuItems,
  globalSearch,
  getGridRef,
  rowData,
  isByContractsGrid
}: AgGridProps) => {
  const gridRef = useRef<any>(null)
  const theme = useTheme()
  const [columnDefs] = useState<any>(
    isByContractsGrid ? [...liquidityRiskByContractsColDef] : [...liquidityRiskDashboardColDef]
  )
  const [gridTheme, setGridTheme] = useState<string>(
    theme.palette.mode === 'dark' ? ' ag-theme-balham-dark' : ' ag-theme-balham'
  )
  useEffect(() => {
    setGridTheme(theme.palette.mode === 'dark' ? ' ag-theme-balham-dark' : ' ag-theme-balham')
  }, [theme.palette.mode])

  let gridApi: any
  const defaultColDef = useMemo<any>(() => {
    return {
      flex: 1,
      sortable: true,
      filter: true,
      floatingFilter: false,
      suppressFilterButton: false,
      editable: false,
      resizable: true,
      headerCheckboxSelectionFilteredOnly: true,
      menuTabs: [constants.FilterMenuTab],
      tooltipShowDelay: 10,
      tooltipMouseTrack: true,
      suppressKeyboardEvent: (params: any) => {
        const isTabKey = params?.event?.key === constants.Tab
        if (isTabKey) gridRef?.current?.api?.stopEditing()

        return false
      }
    }
  }, [])

  useEffect(() => {
    gridApi = gridRef?.current?.api

    const gridColumnApi = gridRef?.current?.columnApi
    aggRowCal(gridApi, gridColumnApi)
  }, [rowData])

  const onGridReady = (params: any) => {
    params.api.sizeColumnsToFit()
    gridApi = params.api

    const gridColumnApi = params.columnApi
    aggRowCal(gridApi, gridColumnApi)

    // Emmits the gridApi refrence to the parent component
    if (getGridApi) {
      getGridApi(gridApi, gridColumnApi)
    }
    getGridRef(gridRef)
  }

  const aggRowCal = (api: any, columnApi: any) => {
    if (!api || !columnApi) return

    const rows: any = {}
    const result = []
    columnApi?.getAllGridColumns().forEach((item: any) => {
      rows[item.colId] = null
    })
    const columnsWithSumAggregation = [
      'dtv',
      'riskadjval',
      'parent_account',
      'daystoliquidate',
      'flt',
      'shares',
      'cb_pd',
      'price',
      'liqrisk',
      'potentiallgd',
      'ewma_vol',
      'cpty_allcoation',
      'value',
      'capcharge'
    ]
    const columnsWithAvgAggregation = ['rate', 'price', 'dtv', 'daystoliquidate', 'flt', 'cb_pd']
    columnsWithSumAggregation.forEach(element => {
      api?.forEachNodeAfterFilter((rowNode: any) => {
        if (rowNode && rowNode.data && rowNode.data[element]) {
          rows[element] += Number(parseFloat(rowNode.data[element]))
        }
      })
    })
    columnsWithAvgAggregation.forEach(element => {
      const nodeCount = api.getDisplayedRowCount()
      const avg = (rows[element] / nodeCount).toString()
      rows[element] = nodeCount !== 0 ? Number(parseFloat(avg).toFixed(2)) : 0
    })
    rows['isTotalLabel'] = true
    rows['name'] = countUnique('name', api)
    rows['ticker'] = countUnique('ticker', api)
    rows['id'] = countUnique('id', api)
    rows['cpty_id'] = countUnique('cpty_id', api)
    rows['cpty_allcoation'] = calcCptyAllocSum(api)
    result.push(rows)
    api?.setPinnedBottomRowData(result)
  }

  const onFilterChanged = (params: FilterChangedEvent) => {
    const { api, columnApi } = params
    aggRowCal(api, columnApi)
  }

  /**
   * Method counts the unique value for given attribute (for filtered values also)
   * @param attribute
   * @returns unique values count
   */
  const countUnique = (attribute: string, api: GridApi): number => {
    const uniqueItemsSet: Set<any> = new Set()
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (rowNode?.data instanceof Object && attribute in rowNode.data) {
        uniqueItemsSet.add(rowNode.data[attribute])
      }
    })

    return uniqueItemsSet.size
  }

  const calcCptyAllocSum = (api: GridApi): number => {
    const rowData: any = []
    const sumValue: any = []
    api?.forEachNodeAfterFilter((node: any) => rowData.push(node.data))
    const groupedData = _.groupBy(rowData, 'name')

    _.forIn(groupedData, (value: any) => {
      if (_.isArray(value) && value?.length > 0) {
        const maxObj = _.maxBy(value, 'cpty_allcoation')

        sumValue?.push(maxObj?.cpty_allcoation)
      }
    })

    return _.sum(sumValue)
  }

  const rowClassRules = useMemo(() => {
    return {
      'is-new-warning': (params: any) => {
        const isNew = params?.data?.is_new

        return isNew === 'TRUE'
      },
      'is-new-recall': (params: any) => {
        const isNew = params?.data?.is_Recall

        return isNew === 'True'
      }
    }
  }, [])

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 100,
      maxWidth: 200
    }
  }, [])

  const getRowId = (params: any) => {
    if (params && params.data) {
      if (params.data.id) {
        return params.data.id
      }
    }

    return makeid(5)
  }

  function makeid(length: any) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    let counter = 0
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
      counter += 1
    }

    return result
  }

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true
          }
        }
      ],
      defaultToolPanel: ''
    }
  }, [])

  function isForceRefreshSelected() {
    return (document.querySelector('#forceRefresh') as HTMLInputElement)?.checked
  }
  function isSuppressFlashSelected() {
    return (document.querySelector('#suppressFlash') as HTMLInputElement)?.checked
  }
  const onGridRowDataChanged = () => {
    const refreshParams: any = {
      force: isForceRefreshSelected(),
      suppressFlash: isSuppressFlashSelected()
    }
    gridRef.current!.api.refreshCells(refreshParams)
  }

  const onDoesfilter = useCallback((node: any) => {
    doesExternalFilterPass(node)

    return true
  }, [])

  return (
    <div
      className={'' + gridTheme}
      style={{
        height: `100%`,
        width: '100%'
      }}
    >
      <AgGridReact
        ref={gridRef}
        defaultColDef={defaultColDef}
        headerHeight={26}
        rowHeight={24}
        rowStyle={{
          justifyContent: 'center',
          color: `${theme.palette.mode === 'dark' ? '' : '#131314'}`,
          fontSize: '0.75rem',
          fontWeight: '400',
          fontStyle: 'normal'
        }}
        columnDefs={columnDefs}
        getRowId={getRowId}
        onRowDataUpdated={onGridRowDataChanged}
        rowData={rowData}
        suppressRowClickSelection={true}
        suppressAggFuncInHeader={true}
        enableRangeSelection={true}
        onGridReady={onGridReady}
        rowClassRules={rowClassRules}
        autoGroupColumnDef={autoGroupColumnDef}
        groupSelectsChildren={true}
        rowGroupPanelShow={`${isByContractsGrid ? 'never' : 'always'}`}
        groupSelectsFiltered={true}
        sideBar={sideBar}
        animateRows={true}
        pivotMode={false}
        cacheQuickFilter={true}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={onDoesfilter}
        getContextMenuItems={getContextMenuItems}
        quickFilterText={globalSearch}
        getRowStyle={theme.palette.mode === 'dark' ? getDarkRowStyle : getRowStyle}
        overlayLoadingTemplate={
          '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>'
        }
        onFilterChanged={onFilterChanged}
      ></AgGridReact>
    </div>
  )
}

export default LiquidityRiskDashboardGrid
